import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  Layers,
  Plus,
  Loader2,
  ExternalLink,
  Pencil,
  Trash2,
  RefreshCw,
  X,
  Power,
} from 'lucide-react';
import { leadSegmentsAPI } from '../utils/api';
import { LeadSegmentResponse } from '../types/leadSegment';
import ConfirmModal from '../components/ConfirmModal';
import { usePermissions } from '../hooks/usePermissions';

interface EditFormState {
  id: string;
  name: string;
  description: string;
  is_active: boolean;
}

function formatDate(iso?: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('it-IT');
}

export default function LeadSegments() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { hasPermission } = usePermissions();
  const canEdit = hasPermission('leads:config:edit');
  const [page, setPage] = useState(1);
  const [previews, setPreviews] = useState<
    Record<string, { loading: boolean; count: number | null; error?: string }>
  >({});
  const [editState, setEditState] = useState<EditFormState | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<LeadSegmentResponse | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['segments', page],
    queryFn: async () => {
      const res = await leadSegmentsAPI.getSegments({ page, limit: 30 });
      return res.data;
    },
  });

  const updateMutation = useMutation({
    mutationFn: (state: EditFormState) =>
      leadSegmentsAPI.updateSegment(state.id, {
        name: state.name,
        description: state.description || undefined,
        is_active: state.is_active,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['segments'] });
      toast.success('Segmento aggiornato');
      setEditState(null);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || "Errore nell'aggiornamento");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => leadSegmentsAPI.deleteSegment(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['segments'] });
      toast.success('Segmento eliminato');
      setDeleteTarget(null);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || "Errore nell'eliminazione");
    },
  });

  const segments = data?.data || [];
  const pagination = data?.pagination;

  const fetchPreview = async (id: string) => {
    setPreviews((prev) => ({ ...prev, [id]: { loading: true, count: null } }));
    try {
      const res = await leadSegmentsAPI.preview(id);
      setPreviews((prev) => ({
        ...prev,
        [id]: { loading: false, count: res.data.count },
      }));
    } catch (e: any) {
      setPreviews((prev) => ({
        ...prev,
        [id]: {
          loading: false,
          count: null,
          error: e.response?.data?.detail || 'Errore',
        },
      }));
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Segmenti Lead</h1>
          <p className="text-gray-600">
            Filtri salvati per raggruppare i lead — riutilizzabili in export e campagne
          </p>
        </div>
        {canEdit && (
          <button
            onClick={() => navigate('/leads')}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Plus className="h-5 w-5" />
            Nuovo segmento (da /leads)
          </button>
        )}
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-900">
        <strong>Come creare un segmento:</strong> vai sulla lista <code>/leads</code>, applica i
        filtri desiderati (stato, source, agente, date range, ecc.) e clicca{' '}
        <em>Salva come segmento</em>. Per modificare i filtri di un segmento esistente,
        cliccane <em>Apri</em>, modifica i filtri e clicca <em>Aggiorna segmento</em>.
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        {isLoading ? (
          <div className="flex justify-center items-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
          </div>
        ) : segments.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <Layers className="h-12 w-12 mx-auto mb-4 text-gray-300" />
            <p>Nessun segmento</p>
            {canEdit && (
              <button
                onClick={() => navigate('/leads')}
                className="mt-4 inline-flex items-center gap-2 text-blue-600 hover:text-blue-800 text-sm font-medium"
              >
                <Plus className="h-4 w-4" />
                Crea il primo segmento dalla lista lead
              </button>
            )}
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Nome
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Descrizione
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Stato
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Match
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ultima modifica
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Azioni
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {segments.map((s) => {
                const preview = previews[s.id];
                return (
                  <tr key={s.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">{s.name}</td>
                    <td className="px-6 py-4 text-sm text-gray-600 max-w-md truncate">
                      {s.description || <span className="text-gray-400">—</span>}
                    </td>
                    <td className="px-6 py-4">
                      {s.is_active ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                          <Power className="h-3 w-3" />
                          Attivo
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-200 text-gray-600">
                          <Power className="h-3 w-3" />
                          Inattivo
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      {preview?.loading ? (
                        <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
                      ) : preview?.count !== undefined && preview?.count !== null ? (
                        <span className="text-sm font-semibold text-gray-900 tabular-nums">
                          {preview.count} lead
                        </span>
                      ) : preview?.error ? (
                        <span className="text-xs text-red-600">{preview.error}</span>
                      ) : (
                        <button
                          type="button"
                          onClick={() => fetchPreview(s.id)}
                          className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800"
                        >
                          <RefreshCw className="h-3 w-3" />
                          Calcola
                        </button>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {formatDate(s.updated_at || s.created_at)}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="inline-flex items-center gap-1">
                        <button
                          onClick={() => navigate(`/leads?segment_id=${s.id}`)}
                          className="inline-flex items-center gap-1 px-2 py-1 text-xs text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded"
                          title="Apri segmento in /leads"
                        >
                          <ExternalLink className="h-3.5 w-3.5" />
                          Apri
                        </button>
                        {canEdit && (
                          <button
                            onClick={() =>
                              setEditState({
                                id: s.id,
                                name: s.name,
                                description: s.description || '',
                                is_active: s.is_active,
                              })
                            }
                            className="p-1 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded"
                            title="Modifica nome/descrizione"
                          >
                            <Pencil className="h-4 w-4" />
                          </button>
                        )}
                        {canEdit && (
                          <button
                            onClick={() => setDeleteTarget(s)}
                            className="p-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded"
                            title="Elimina"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        {pagination && pagination.pages > 1 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200">
            <div className="text-sm text-gray-700">
              Pagina {pagination.page} di {pagination.pages} ({pagination.total} segmenti)
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setPage(page - 1)}
                disabled={page === 1}
                className="px-3 py-1 border border-gray-300 rounded-md disabled:opacity-50"
              >
                Precedente
              </button>
              <button
                onClick={() => setPage(page + 1)}
                disabled={page >= pagination.pages}
                className="px-3 py-1 border border-gray-300 rounded-md disabled:opacity-50"
              >
                Successivo
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Edit modal */}
      {editState && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="flex justify-between items-center p-6 border-b">
              <h2 className="text-xl font-semibold">Modifica segmento</h2>
              <button
                onClick={() => setEditState(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                updateMutation.mutate(editState);
              }}
              className="p-6 space-y-4"
            >
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nome *</label>
                <input
                  type="text"
                  value={editState.name}
                  onChange={(e) => setEditState({ ...editState, name: e.target.value })}
                  required
                  maxLength={200}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Descrizione
                </label>
                <textarea
                  value={editState.description}
                  onChange={(e) =>
                    setEditState({ ...editState, description: e.target.value })
                  }
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <label className="inline-flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={editState.is_active}
                  onChange={(e) =>
                    setEditState({ ...editState, is_active: e.target.checked })
                  }
                  className="h-4 w-4 rounded border-gray-300"
                />
                Segmento attivo
              </label>
              <p className="text-xs text-gray-500">
                Per modificare i filtri, apri il segmento in <code>/leads</code>, cambia i
                filtri e premi <em>Aggiorna segmento</em> nel filter bar.
              </p>
              <div className="flex justify-end gap-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => setEditState(null)}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm"
                >
                  Annulla
                </button>
                <button
                  type="submit"
                  disabled={updateMutation.isPending}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm"
                >
                  {updateMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                  Salva
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {deleteTarget && (
        <ConfirmModal
          isOpen={!!deleteTarget}
          title="Elimina segmento"
          message={`Eliminare il segmento "${deleteTarget.name}"? I lead non vengono rimossi, solo la query salvata.`}
          confirmLabel="Elimina"
          onConfirm={() => deleteMutation.mutate(deleteTarget.id)}
          onClose={() => setDeleteTarget(null)}
          isLoading={deleteMutation.isPending}
          variant="danger"
        />
      )}
    </div>
  );
}
